import {
  Children,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type SVGProps,
} from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useScroll,
  useTransform,
  type MotionValue,
} from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { CountUp } from './CountUp';
import { alaViewport } from './alaMotion';
import { plainText } from './propertyUtils';

const ALA_ICON =
  '';

const PROPERTY_STRIP_DEFAULT =
  '';

const why2ImageDef: SlotGroupDefinition = {
  label: 'Why Card 2 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Disciplined Investment Decisions' },
  },
};

const why4ImageDef: SlotGroupDefinition = {
  label: 'Why Card 4 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Hands-On Management' },
  },
};

const propertyStripDef: SlotGroupDefinition = {
  label: 'Property Strip',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Strip' },
  },
};

const cap1ImageDef: SlotGroupDefinition = {
  label: 'Capability 1 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Investments' },
  },
};

const cap2ImageDef: SlotGroupDefinition = {
  label: 'Capability 2 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Construction Management & Design' },
  },
};

const cap3ImageDef: SlotGroupDefinition = {
  label: 'Capability 3 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management' },
  },
};

const cap4ImageDef: SlotGroupDefinition = {
  label: 'Capability 4 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Portfolio Management' },
  },
};

const cap5ImageDef: SlotGroupDefinition = {
  label: 'Capability 5 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Revenue Management' },
  },
};

const cap6ImageDef: SlotGroupDefinition = {
  label: 'Capability 6 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Capital Raising' },
  },
};

const contactCtaDef: SlotGroupDefinition = {
  label: 'Contact CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const partnerCtaDef: SlotGroupDefinition = {
  label: 'Partner CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Partner with us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const PILLAR_TAG_DEFAULTS = [
  'Market Selection & Sourcing|Underwriting & Analysis|Due Diligence|Capital Structuring|Acquisition Strategy',
  'Construction Management|Interior Design & Renovation|Capital Project Planning|Vendor & Cost Management|Quality Assurance',
  'Property Management|Portfolio Management|Revenue Management|Resident Experience|Capital Raising & Investor Relations',
] as const;

const FAQ_DEFAULTS = [
  {
    q: 'What services does American Landmark offer?',
    a: 'American Landmark is a fully integrated multifamily platform. We provide in-house expertise across investments, construction management and design, property management, portfolio management, revenue management, and capital raising. Every capability is housed under one roof, giving us greater control and accountability at every stage of the asset lifecycle.',
  },
  {
    q: 'How does vertical integration benefit investors?',
    a: 'Vertical integration means we control every aspect of the investment—from acquisition and underwriting through construction, operations, and disposition. This reduces reliance on third parties, lowers costs, speeds up execution, and ensures every decision aligns with our overall strategy. The result is better risk-adjusted returns and more transparent reporting for our investors.',
  },
  {
    q: 'What markets does American Landmark focus on?',
    a: 'We focus on high-growth Sunbelt markets with strong economic fundamentals, job growth, and long-term demand for multifamily housing. Our portfolio spans eight states across the Southeast and Sunbelt, where our local expertise helps us identify opportunities with lasting potential.',
  },
  {
    q: 'Can American Landmark manage properties we already own?',
    a: 'Our property management capabilities are primarily dedicated to communities within our own portfolio. However, we welcome conversations with property owners and investors who are interested in partnering with us on acquisitions or joint ventures where our integrated platform can add meaningful value.',
  },
  {
    q: 'How does American Landmark approach value-add renovations?',
    a: 'Our in-house construction and design teams develop tailored renovation plans for each community based on market analysis, resident feedback, and return-on-investment modeling. We prioritize high-impact improvements—both interior and exterior—that elevate the resident experience, improve curb appeal, and strengthen long-term asset value.',
  },
] as const;

function splitTags(value: string): string[] {
  return value
    .split('|')
    .map((t) => t.trim())
    .filter(Boolean);
}

function PillarIconDiamond(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 120 120" fill="none" aria-hidden {...props}>
      <rect x="30" y="30" width="60" height="60" transform="rotate(45 60 60)" stroke="currentColor" strokeWidth="3" />
      <rect x="42" y="42" width="36" height="36" transform="rotate(45 60 60)" stroke="currentColor" strokeWidth="3" />
      <rect x="52" y="52" width="16" height="16" transform="rotate(45 60 60)" fill="currentColor" />
    </svg>
  );
}

function PillarIconHex(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 120 120" fill="none" aria-hidden {...props}>
      <polygon points="60,12 102,36 102,84 60,108 18,84 18,36" stroke="currentColor" strokeWidth="3" />
      <polygon points="60,34 84,48 84,72 60,86 36,72 36,48" stroke="currentColor" strokeWidth="3" />
      <circle cx="60" cy="60" r="8" fill="currentColor" />
    </svg>
  );
}

function PillarIconOrbit(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 120 120" fill="none" aria-hidden {...props}>
      <circle cx="60" cy="60" r="42" stroke="currentColor" strokeWidth="3" />
      <circle cx="60" cy="18" r="9" fill="currentColor" />
      <circle cx="23" cy="80" r="9" fill="currentColor" />
      <circle cx="97" cy="80" r="9" fill="currentColor" />
      <circle cx="60" cy="60" r="8" fill="currentColor" />
    </svg>
  );
}

const PILLAR_ICONS = [PillarIconDiamond, PillarIconHex, PillarIconOrbit] as const;

function CtaLink({
  href,
  target,
  children,
  className = '',
}: {
  href: string;
  target?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <a
      href={href || '/contact-us'}
      target={target || '_self'}
      className={`group inline-flex w-fit items-center justify-center gap-2 rounded-full bg-[rgb(var(--ala-secondary))] px-5 py-[15px] font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-secondary))] transition-colors hover:bg-[#3a85d0] ${className}`}
    >
      <span>{children}</span>
      <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" strokeWidth={2} />
    </a>
  );
}

function ScrollRevealWords({
  text,
  className,
  highlightWords,
  highlightClassName,
}: {
  text: string;
  className?: string;
  highlightWords: string[];
  highlightClassName?: string;
}) {
  const ref = useRef<HTMLParagraphElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 0.85', 'start 0.25'],
  });
  const words = text.split(' ');
  const highlights = new Set(highlightWords.map((w) => w.toLowerCase()));

  return (
    <p ref={ref} className={className}>
      {words.map((word, i) => {
        const start = i / words.length;
        const end = start + 1 / words.length;
        const cleaned = word.toLowerCase().replace(/[.,'—–-]/g, '');
        const isHighlight = highlights.has(cleaned);
        return (
          <ScrollWord
            key={`${word}-${i}`}
            word={word}
            progress={scrollYProgress}
            range={[start, end]}
            isHighlight={isHighlight}
            highlightClassName={highlightClassName}
          />
        );
      })}
    </p>
  );
}

function ScrollWord({
  word,
  progress,
  range,
  isHighlight,
  highlightClassName,
}: {
  word: string;
  progress: MotionValue<number>;
  range: [number, number];
  isHighlight: boolean;
  highlightClassName?: string;
}) {
  const opacity = useTransform(progress, range, [0.12, 1]);
  return (
    <span className="mr-[0.25em] inline-block">
      <motion.span
        style={{ opacity }}
        className={
          isHighlight
            ? highlightClassName || 'font-medium text-[rgb(var(--ala-secondary))]'
            : 'font-heading font-medium'
        }
      >
        {word}
      </motion.span>
    </span>
  );
}

function PillarsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end'],
  });

  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    const next = Math.min(2, Math.max(0, Math.round(v * 2)));
    setActive(next);
  });

  const ActiveIcon = PILLAR_ICONS[active % PILLAR_ICONS.length];

  const title1 =
    plainText(useContentToken('strategycontent.pillar_1_title')) || 'Invest with clarity';
  const title2 =
    plainText(useContentToken('strategycontent.pillar_2_title')) || 'Build with purpose';
  const title3 =
    plainText(useContentToken('strategycontent.pillar_3_title')) || 'Manage with excellence';
  const tags1 = splitTags(
    plainText(useContentToken('strategycontent.pillar_1_tags')) || PILLAR_TAG_DEFAULTS[0],
  );
  const tags2 = splitTags(
    plainText(useContentToken('strategycontent.pillar_2_tags')) || PILLAR_TAG_DEFAULTS[1],
  );
  const tags3 = splitTags(
    plainText(useContentToken('strategycontent.pillar_3_tags')) || PILLAR_TAG_DEFAULTS[2],
  );

  const pillars = [
    { title: title1, tags: tags1 },
    { title: title2, tags: tags2 },
    { title: title3, tags: tags3 },
  ];

  return (
    <section ref={sectionRef} className="relative w-full bg-[rgb(var(--ala-background))]">
      <div className="mx-auto flex max-w-[1440px] flex-col lg:flex-row">
        <div className="sticky top-0 hidden h-screen w-1/2 items-center justify-center p-12 lg:flex">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, scale: 0.8, rotate: -8 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.8, rotate: 8 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="h-[220px] w-[220px] text-[rgb(var(--ala-secondary))]"
            >
              <ActiveIcon className="h-full w-full" />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="w-full lg:w-1/2">
          {pillars.map((pillar, i) => {
            const Icon = PILLAR_ICONS[i % PILLAR_ICONS.length];
            const parts = pillar.title.split(' ');
            const first = parts[0] || '';
            const rest = parts.slice(1).join(' ');
            return (
              <div
                key={pillar.title}
                className="flex min-h-screen flex-col justify-center gap-8 p-8 md:p-12 lg:p-16 lg:py-24"
              >
                <div className="mb-2 h-20 w-20 text-[rgb(var(--ala-secondary))] lg:hidden">
                  <Icon className="h-full w-full" />
                </div>
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.7 }}
                  className="font-heading text-[clamp(36px,5vw,72px)] font-medium leading-[1.0] tracking-[-0.03em] text-[rgb(var(--ala-text))]"
                >
                  <span className="text-[rgb(var(--ala-secondary))]">{first}</span>
                  {rest ? ` ${rest}` : ''}
                </motion.h2>
                <span className="sr-only">
                  <RichTextContent
                    path={`strategycontent.pillar_${i + 1}_title`}
                    label={`Pillar ${i + 1} Title`}
                    as="span"
                  />
                </span>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.7, delay: 0.1 }}
                >
                  <RichTextContent
                    path={`strategycontent.pillar_${i + 1}_body`}
                    label={`Pillar ${i + 1} Body`}
                    as="p"
                    className="max-w-[520px] font-heading text-[clamp(17px,1.5vw,22px)] font-normal leading-[1.6] text-[#4a4a4a]"
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.7, delay: 0.2 }}
                  className="flex max-w-[520px] flex-wrap gap-3"
                >
                  {pillar.tags.map((tag) => (
                    <span
                      key={tag}
                      className="cursor-default rounded-full border border-[#c0c0c0] px-5 py-2.5 font-heading text-base font-medium text-[rgb(var(--ala-text))] transition-colors hover:border-[rgb(var(--ala-secondary))] hover:text-[rgb(var(--ala-secondary))]"
                    >
                      {tag}
                    </span>
                  ))}
                  <span className="sr-only">
                    <RichTextContent
                      path={`strategycontent.pillar_${i + 1}_tags`}
                      label={`Pillar ${i + 1} Tags`}
                      as="span"
                    />
                  </span>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WhyScrollStack({ header, children }: { header: ReactNode; children: ReactNode }) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const items = Children.toArray(children);
  const count = items.length;

  if (isMobile) {
    return (
      <section className="w-full bg-[rgb(var(--ala-dark))]">
        {header ? <div className="mx-auto w-full max-w-[1440px] px-6 pb-6 pt-14">{header}</div> : null}
        <div className="flex flex-col gap-5 px-6 pb-24">{items}</div>
      </section>
    );
  }

  return (
    <section className="strategy-scroll-root w-full bg-[rgb(var(--ala-dark))]">
      <div className="strategy-scroll-sticky">
        {header ? (
          <div className="mx-auto w-full max-w-[1440px] px-6 pb-6 pt-14 lg:px-[clamp(16px,4.7vw,68px)] lg:pb-10 lg:pt-20">
            {header}
          </div>
        ) : null}
        <div className="strategy-scroll-track">
          {items.map((child, i) => (
            <div
              key={i}
              className={`strategy-card${i === 0 ? ' strategy-card-first' : ''}`}
              style={
                {
                  zIndex: i + 1,
                  ...(i > 0
                    ? {
                        animationRange: `contain ${(((i - 1) / (count - 1)) * 100).toFixed(2)}% contain ${((i / (count - 1)) * 100).toFixed(2)}%`,
                      }
                    : {}),
                } as CSSProperties
              }
            >
              {child}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyCard({
  variant,
  titlePath,
  titleLabel,
  bodyPath,
  bodyLabel,
  imageGroupPath,
  imageDef,
  image,
  imageAlt,
}: {
  variant: 'red' | 'blue' | 'image';
  titlePath: string;
  titleLabel: string;
  bodyPath: string;
  bodyLabel: string;
  imageGroupPath?: string;
  imageDef?: SlotGroupDefinition;
  image?: string;
  imageAlt?: string;
}) {
  const isImage = variant === 'image';
  const bg =
    variant === 'red' ? 'bg-[#e3002f]' : variant === 'blue' ? 'bg-[rgb(var(--ala-secondary))]' : 'group';

  return (
    <div
      className={`relative flex h-full min-h-[340px] w-full shrink-0 flex-col justify-between overflow-clip p-8 lg:min-h-0 lg:p-[30px] ${bg}`}
    >
      {isImage && imageGroupPath && imageDef ? (
        <EditableSlotGroup groupPath={imageGroupPath} definition={imageDef}>
          <VibesitesImage
            url={image || ''}
            alt={imageAlt || ''}
            sizes="(max-width: 1024px) 100vw, 80vw"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black/40" />
        </EditableSlotGroup>
      ) : null}
      <div className="relative z-10">
        <VibesitesImage
          url={ALA_ICON}
          alt=""
          sizes="54px"
          className="h-[54px] w-[54px] shrink-0 object-contain"
        />
      </div>
      <div className="relative z-10 mt-auto flex flex-col gap-3">
        <RichTextContent
          path={titlePath}
          label={titleLabel}
          as="h3"
          className="font-heading text-[clamp(22px,2.78vw,40px)] font-medium leading-[1.1] tracking-[-0.02em] text-[rgb(var(--ala-on-primary))]"
        />
        <RichTextContent
          path={bodyPath}
          label={bodyLabel}
          as="p"
          className={`font-heading text-lg font-normal leading-[1.4] ${
            variant === 'blue' ? 'text-[rgb(var(--ala-dark))]/80' : 'text-[rgb(var(--ala-on-primary))]/90'
          }`}
        />
      </div>
    </div>
  );
}

function CapabilityRow({
  index,
  titlePath,
  titleLabel,
  bodyPath,
  bodyLabel,
  imageGroupPath,
  imageDef,
  image,
  imageAlt,
  learnMoreHref,
  learnMoreLabel,
  learnMoreTarget,
}: {
  index: number;
  titlePath: string;
  titleLabel: string;
  bodyPath: string;
  bodyLabel: string;
  imageGroupPath: string;
  imageDef: SlotGroupDefinition;
  image: string;
  imageAlt: string;
  learnMoreHref: string;
  learnMoreLabel: string;
  learnMoreTarget: string;
}) {
  const [open, setOpen] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const title =
    plainText(useContentToken(titlePath)) ||
    [
      'Investments',
      'Construction Management & Design',
      'Property Management',
      'Portfolio Management',
      'Revenue Management',
      'Capital Raising',
    ][index - 1];

  useEffect(() => {
    const mq = window.matchMedia('(hover: none)');
    const update = () => setIsTouch(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const toggle = () => setOpen((v) => !v);

  return (
    <div
      onMouseEnter={() => {
        if (!isTouch) setOpen(true);
      }}
      onMouseLeave={() => {
        if (!isTouch) setOpen(false);
      }}
      onClick={() => {
        if (isTouch) toggle();
      }}
      className={`-mx-4 w-full rounded-lg border-t border-[rgba(8,13,13,0.10)] px-4 transition-colors duration-300 ${
        open ? 'bg-[#ebebeb]' : 'bg-transparent'
      } ${isTouch ? 'cursor-pointer' : ''}`}
    >
      <div className="flex flex-col items-start justify-between gap-5 py-6 md:flex-row md:items-center md:gap-6 md:py-8 lg:py-[30px]">
        <div className="flex items-start gap-5 md:gap-8">
          <span className="pt-1 font-heading text-lg font-normal leading-[1.4] text-[#9a9a9a]">
            {String(index).padStart(2, '0')}
          </span>
          <div className="flex flex-col gap-2">
            <p className="font-heading text-lg font-normal leading-[1.4] text-[#7a7a7a]">ALA Strategy</p>
            <h3 className="font-heading text-[clamp(24px,2.36vw,34px)] font-medium leading-[1.2941] tracking-[-0.0088em] text-[rgb(var(--ala-text))]">
              {title}
            </h3>
            <span className="sr-only">
              <RichTextContent path={titlePath} label={titleLabel} as="span" />
            </span>
          </div>
        </div>
        {isTouch ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggle();
            }}
            aria-label={open ? 'Collapse details' : 'Expand details'}
            className="flex h-10 w-10 shrink-0 items-center justify-center self-end rounded-full shadow-[inset_0_0_0_1px_rgba(0,0,0,0.15)] md:self-auto"
          >
            <ChevronDown
              className={`h-5 w-5 text-[rgb(var(--ala-text))] transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
            />
          </button>
        ) : (
          <CtaLink href={learnMoreHref} target={learnMoreTarget} className="shrink-0">
            {learnMoreLabel || 'Learn More'}
          </CtaLink>
        )}
      </div>

      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="flex flex-col items-start gap-6 pb-8 pl-0 md:flex-row md:pl-[44px] lg:gap-10 lg:pb-10 lg:pl-[56px]">
              <EditableSlotGroup groupPath={imageGroupPath} definition={imageDef}>
                <div className="aspect-[4/3] w-full shrink-0 overflow-clip rounded-[12px] md:w-[280px]">
                  <VibesitesImage
                    url={image}
                    alt={imageAlt || title}
                    sizes="(max-width: 768px) 100vw, 280px"
                    className="h-full w-full object-cover"
                  />
                </div>
              </EditableSlotGroup>
              <RichTextContent
                path={bodyPath}
                label={bodyLabel}
                as="p"
                className="max-w-[560px] font-heading text-base font-normal leading-[1.4] text-[#4a4a4a]"
              />
            </div>
          </motion.div>
        ) : (
          <span className="sr-only">
            <RichTextContent path={bodyPath} label={bodyLabel} as="span" />
            <EditableSlotGroup groupPath={imageGroupPath} definition={imageDef}>
              <span />
            </EditableSlotGroup>
          </span>
        )}
      </AnimatePresence>
    </div>
  );
}

export function StrategyContent() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const contactCta = useSlotGroup('strategycontent.contact_cta', contactCtaDef);
  const partnerCta = useSlotGroup('strategycontent.partner_cta', partnerCtaDef);
  const why2Image = useSlotGroup('strategycontent.why_2_image', why2ImageDef);
  const why4Image = useSlotGroup('strategycontent.why_4_image', why4ImageDef);
  const propertyStrip = useSlotGroup('strategycontent.property_strip', propertyStripDef);
  const cap1Image = useSlotGroup('strategycontent.cap_1_image', cap1ImageDef);
  const cap2Image = useSlotGroup('strategycontent.cap_2_image', cap2ImageDef);
  const cap3Image = useSlotGroup('strategycontent.cap_3_image', cap3ImageDef);
  const cap4Image = useSlotGroup('strategycontent.cap_4_image', cap4ImageDef);
  const cap5Image = useSlotGroup('strategycontent.cap_5_image', cap5ImageDef);
  const cap6Image = useSlotGroup('strategycontent.cap_6_image', cap6ImageDef);

  const advantageText =
    plainText(useContentToken('strategycontent.advantage_body')) ||
    "Build value that endures. An integrated approach to real estate means every team, every decision, and every dollar works together—so your assets don't just perform today, they compound for years to come.";

  const faqItems = [
    {
      q: plainText(useContentToken('strategycontent.faq_1_q')) || FAQ_DEFAULTS[0].q,
      a: plainText(useContentToken('strategycontent.faq_1_a')) || FAQ_DEFAULTS[0].a,
    },
    {
      q: plainText(useContentToken('strategycontent.faq_2_q')) || FAQ_DEFAULTS[1].q,
      a: plainText(useContentToken('strategycontent.faq_2_a')) || FAQ_DEFAULTS[1].a,
    },
    {
      q: plainText(useContentToken('strategycontent.faq_3_q')) || FAQ_DEFAULTS[2].q,
      a: plainText(useContentToken('strategycontent.faq_3_a')) || FAQ_DEFAULTS[2].a,
    },
    {
      q: plainText(useContentToken('strategycontent.faq_4_q')) || FAQ_DEFAULTS[3].q,
      a: plainText(useContentToken('strategycontent.faq_4_a')) || FAQ_DEFAULTS[3].a,
    },
    {
      q: plainText(useContentToken('strategycontent.faq_5_q')) || FAQ_DEFAULTS[4].q,
      a: plainText(useContentToken('strategycontent.faq_5_a')) || FAQ_DEFAULTS[4].a,
    },
  ];

  const faqJsonKey = faqItems.map((f) => `${f.q}|${f.a}`).join('||');

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    });
    script.setAttribute('data-faq-jsonld', 'services-faq');
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
    // faqItems derived from content tokens; key tracks content changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faqJsonKey]);

  const stat1Value = plainText(useContentToken('strategycontent.stat_1_value')) || '10,000+';
  const stat2Value = plainText(useContentToken('strategycontent.stat_2_value')) || '30+';
  const stat3Value = plainText(useContentToken('strategycontent.stat_3_value')) || '8';
  const stat4Value = plainText(useContentToken('strategycontent.stat_4_value')) || '35+';
  const stat5Value = plainText(useContentToken('strategycontent.stat_5_value')) || '500+';
  const stat6Value = plainText(useContentToken('strategycontent.stat_6_value')) || '100%';

  const stats = [
    { valuePath: 'strategycontent.stat_1_value', labelPath: 'strategycontent.stat_1_label', value: stat1Value },
    { valuePath: 'strategycontent.stat_2_value', labelPath: 'strategycontent.stat_2_label', value: stat2Value },
    { valuePath: 'strategycontent.stat_3_value', labelPath: 'strategycontent.stat_3_label', value: stat3Value },
    { valuePath: 'strategycontent.stat_4_value', labelPath: 'strategycontent.stat_4_label', value: stat4Value },
    { valuePath: 'strategycontent.stat_5_value', labelPath: 'strategycontent.stat_5_label', value: stat5Value },
    { valuePath: 'strategycontent.stat_6_value', labelPath: 'strategycontent.stat_6_label', value: stat6Value },
  ];

  const capImages = [
    { group: 'strategycontent.cap_1_image', def: cap1ImageDef, data: cap1Image },
    { group: 'strategycontent.cap_2_image', def: cap2ImageDef, data: cap2Image },
    { group: 'strategycontent.cap_3_image', def: cap3ImageDef, data: cap3Image },
    { group: 'strategycontent.cap_4_image', def: cap4ImageDef, data: cap4Image },
    { group: 'strategycontent.cap_5_image', def: cap5ImageDef, data: cap5Image },
    { group: 'strategycontent.cap_6_image', def: cap6ImageDef, data: cap6Image },
  ];

  const faqQClass =
    'font-heading text-[clamp(18px,1.8vw,24px)] font-semibold text-[rgb(var(--ala-text))] transition-colors hover:text-[rgb(var(--ala-secondary))]';
  const faqAClass =
    'pb-6 font-heading text-base font-normal leading-6 text-[rgb(var(--ala-text))]/80';

  return (
    <>
      {/* Intro */}
      <section className="w-full bg-[rgb(var(--ala-background))] px-6 py-24 md:px-[clamp(16px,6vw,87px)] lg:py-32">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-20">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8 }}
            className="font-heading text-[clamp(36px,5.2vw,76px)] font-medium leading-[1.0] tracking-[-0.03em] text-[rgb(var(--ala-text))]"
          >
            <RichTextContent
              path="strategycontent.intro_headline"
              label="Intro Headline"
              as="span"
              className="[&_span]:text-[rgb(var(--ala-secondary))]"
            />
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="flex flex-col gap-6 lg:pt-3"
          >
            <RichTextContent
              path="strategycontent.intro_body_1"
              label="Intro Body 1"
              as="p"
              className="max-w-[560px] font-heading text-[clamp(17px,1.5vw,22px)] font-normal leading-[1.6] text-[#4a4a4a]"
            />
            <RichTextContent
              path="strategycontent.intro_body_2"
              label="Intro Body 2"
              as="p"
              className="max-w-[560px] font-heading text-[clamp(17px,1.5vw,22px)] font-normal leading-[1.6] text-[#4a4a4a]"
            />
          </motion.div>
        </div>
      </section>

      <PillarsSection />

      {/* Why strategy delivers */}
      <WhyScrollStack
        header={
          <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end lg:gap-10">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={alaViewport}
              transition={{ duration: 0.8 }}
            >
              <RichTextContent
                path="strategycontent.why_headline"
                label="Why Headline"
                as="h2"
                className="max-w-[667px] font-heading text-[clamp(42px,5.28vw,76px)] font-medium leading-[1.1053] tracking-[-0.05em] text-[rgb(var(--ala-on-primary))]"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={alaViewport}
              transition={{ duration: 0.8 }}
              className="flex flex-col items-start gap-5"
            >
              <RichTextContent
                path="strategycontent.why_body"
                label="Why Body"
                as="p"
                className="max-w-[440px] font-heading text-lg font-normal leading-[1.4] text-[#a0aab2]"
              />
              <EditableSlotGroup groupPath="strategycontent.contact_cta" definition={contactCtaDef}>
                <CtaLink href={contactCta.url || '/contact-us'} target={contactCta._target || undefined}>
                  {contactCta.label || 'Contact us'}
                </CtaLink>
              </EditableSlotGroup>
            </motion.div>
          </div>
        }
      >
        <WhyCard
          variant="red"
          titlePath="strategycontent.why_1_title"
          titleLabel="Why 1 Title"
          bodyPath="strategycontent.why_1_body"
          bodyLabel="Why 1 Body"
        />
        <WhyCard
          variant="image"
          titlePath="strategycontent.why_2_title"
          titleLabel="Why 2 Title"
          bodyPath="strategycontent.why_2_body"
          bodyLabel="Why 2 Body"
          imageGroupPath="strategycontent.why_2_image"
          imageDef={why2ImageDef}
          image={why2Image.image || undefined}
          imageAlt={why2Image.alt || undefined}
        />
        <WhyCard
          variant="blue"
          titlePath="strategycontent.why_3_title"
          titleLabel="Why 3 Title"
          bodyPath="strategycontent.why_3_body"
          bodyLabel="Why 3 Body"
        />
        <WhyCard
          variant="image"
          titlePath="strategycontent.why_4_title"
          titleLabel="Why 4 Title"
          bodyPath="strategycontent.why_4_body"
          bodyLabel="Why 4 Body"
          imageGroupPath="strategycontent.why_4_image"
          imageDef={why4ImageDef}
          image={why4Image.image || undefined}
          imageAlt={why4Image.alt || undefined}
        />
      </WhyScrollStack>

      {/* Property strip */}
      <section className="relative z-10 mx-auto mb-16 mt-16 w-full max-w-[1440px] px-6 lg:mb-0 lg:mt-24 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={alaViewport}
          transition={{ duration: 0.8 }}
          className="aspect-[16/9] w-full overflow-clip shadow-2xl lg:aspect-[1272/459]"
        >
          <EditableSlotGroup groupPath="strategycontent.property_strip" definition={propertyStripDef}>
            <VibesitesImage
              url={propertyStrip.image || PROPERTY_STRIP_DEFAULT}
              alt={propertyStrip.alt || 'Property Strip'}
              sizes="(max-width: 1440px) 100vw, 1440px"
              className="h-full w-full object-cover object-center"
            />
          </EditableSlotGroup>
        </motion.div>
      </section>

      {/* Capabilities */}
      <section className="mx-auto flex w-full max-w-[1440px] flex-col items-center gap-16 px-6 pb-24 pt-12 lg:gap-[clamp(20px,6.9vw,100px)] lg:px-[clamp(16px,4.7vw,68px)] lg:pb-[clamp(35px,9.7vw,140px)] lg:pt-[clamp(35px,9.7vw,140px)]">
        <div className="flex max-w-[796px] flex-col items-center gap-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.6 }}
          >
            <RichTextContent
              path="strategycontent.platform_headline"
              label="Platform Headline"
              as="h2"
              className="font-heading text-[clamp(42px,5.28vw,76px)] font-semibold leading-[1.1053] tracking-[-0.05em] text-[rgb(var(--ala-text))]"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <RichTextContent
              path="strategycontent.platform_body"
              label="Platform Body"
              as="p"
              className="max-w-[710px] font-heading text-base font-normal leading-[1.4] text-[#4a4a4a]"
            />
          </motion.div>
        </div>

        <div className="flex w-full flex-col">
          {capImages.map((cap, i) => (
            <motion.div
              key={cap.group}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={alaViewport}
              transition={{ duration: 0.5, delay: i * 0.08 }}
            >
              <CapabilityRow
                index={i + 1}
                titlePath={`strategycontent.cap_${i + 1}_title`}
                titleLabel={`Capability ${i + 1} Title`}
                bodyPath={`strategycontent.cap_${i + 1}_body`}
                bodyLabel={`Capability ${i + 1} Body`}
                imageGroupPath={cap.group}
                imageDef={cap.def}
                image={cap.data.image || ''}
                imageAlt={cap.data.alt || ''}
                learnMoreHref={partnerCta.url || '/contact-us'}
                learnMoreLabel="Learn More"
                learnMoreTarget={partnerCta._target || '_self'}
              />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Integrated advantage */}
      <section className="w-full bg-[rgb(var(--ala-dark))] px-6 py-24 md:px-[clamp(16px,6vw,87px)] lg:py-40">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            className="flex items-center gap-3"
          >
            <span className="h-[9px] min-h-[9px] w-[9px] rounded-full bg-[rgb(var(--ala-secondary))]" aria-hidden />
            <RichTextContent
              path="strategycontent.advantage_eyebrow"
              label="Advantage Eyebrow"
              as="p"
              className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-on-primary))]/60"
            />
          </motion.div>

          <ScrollRevealWords
            text={advantageText}
            highlightWords={['integrated', 'together', 'compound', 'endures']}
            highlightClassName="font-medium text-[rgb(var(--ala-secondary))]"
            className="max-w-[1200px] font-heading text-[clamp(32px,4.2vw,64px)] font-bold leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
          />
          <span className="sr-only">
            <RichTextContent path="strategycontent.advantage_body" label="Advantage Body" as="span" />
          </span>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ delay: 0.2 }}
            className="mt-4"
          >
            <EditableSlotGroup groupPath="strategycontent.partner_cta" definition={partnerCtaDef}>
              <CtaLink href={partnerCta.url || '/contact-us'} target={partnerCta._target || undefined}>
                {partnerCta.label || 'Partner with us'}
              </CtaLink>
            </EditableSlotGroup>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="w-full bg-[rgb(var(--ala-dark))] px-6 py-20 md:px-[clamp(16px,6vw,87px)] lg:py-32">
        <div className="mx-auto max-w-[1440px]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            className="mb-16 max-w-[700px]"
          >
            <div className="mb-6 flex items-center gap-3">
              <span className="h-[9px] min-h-[9px] w-[9px] rounded-full bg-[rgb(var(--ala-secondary))]" aria-hidden />
              <RichTextContent
                path="strategycontent.stats_eyebrow"
                label="Stats Eyebrow"
                as="p"
                className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-on-primary))]/60"
              />
            </div>
            <RichTextContent
              path="strategycontent.stats_headline"
              label="Stats Headline"
              as="h2"
              className="font-heading text-[clamp(36px,5.2vw,76px)] font-medium leading-[1.0] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
            />
          </motion.div>

          <div className="grid grid-cols-1 gap-px bg-[rgb(var(--ala-on-primary))]/10 md:grid-cols-2 lg:grid-cols-3">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.valuePath}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
                className="flex min-h-[260px] flex-col justify-between gap-8 bg-[rgb(var(--ala-dark))] p-8 lg:p-12"
              >
                <CountUp
                  value={stat.value}
                  className="font-heading text-[clamp(50px,6vw,96px)] font-bold leading-none tracking-[-0.04em] text-[rgb(var(--ala-on-primary))]"
                />
                <span className="sr-only">
                  <RichTextContent path={stat.valuePath} label={`Stat ${i + 1} Value`} as="span" />
                </span>
                <RichTextContent
                  path={stat.labelPath}
                  label={`Stat ${i + 1} Label`}
                  as="p"
                  className="max-w-[220px] font-heading text-lg font-normal leading-[1.4] text-[rgb(var(--ala-on-primary))]/60"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="w-full bg-[rgb(var(--ala-background))] px-6 py-24 md:px-12">
        <div className="mx-auto max-w-[900px]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            className="mb-12 flex flex-col gap-3 text-center"
          >
            <div className="flex items-center justify-center gap-3">
              <span className="h-[9px] min-h-[9px] w-[9px] rounded-full bg-[rgb(var(--ala-accent))]" aria-hidden />
              <RichTextContent
                path="strategycontent.faq_eyebrow"
                label="FAQ Eyebrow"
                as="p"
                className="font-heading font-medium uppercase leading-7 tracking-[2px] text-[rgb(var(--ala-text))]"
              />
            </div>
            <RichTextContent
              path="strategycontent.faq_headline"
              label="FAQ Headline"
              as="h2"
              className="font-heading text-[clamp(38px,4.86vw,70px)] font-bold leading-[1.05] tracking-[-0.02em] text-[rgb(var(--ala-text))]"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ delay: 0.1 }}
            className="w-full"
          >
            {faqItems.map((_, i) => (
              <div key={i} className="border-b border-[rgb(var(--ala-text))]/15">
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  className="flex w-full items-center justify-between gap-4 py-6 text-left"
                >
                  <RichTextContent
                    path={`strategycontent.faq_${i + 1}_q`}
                    label={`FAQ ${i + 1} Question`}
                    as="span"
                    className={faqQClass}
                  />
                  <ChevronDown
                    className={`h-5 w-5 shrink-0 text-[rgb(var(--ala-text-muted))] transition-transform duration-300 ${
                      openFaq === i ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {openFaq === i ? (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <RichTextContent
                        path={`strategycontent.faq_${i + 1}_a`}
                        label={`FAQ ${i + 1} Answer`}
                        as="p"
                        className={faqAClass}
                      />
                    </motion.div>
                  ) : (
                    <span className="sr-only">
                      <RichTextContent
                        path={`strategycontent.faq_${i + 1}_a`}
                        label={`FAQ ${i + 1} Answer`}
                        as="span"
                      />
                    </span>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Schema anchors — literal paths for tokens/slot groups rendered via props or templates */}
      <div className="hidden" aria-hidden style={{ display: 'none' }}>
        <RichTextContent path="strategycontent.pillar_1_title" label="Pillar 1 Title" as="span" />
        <RichTextContent path="strategycontent.pillar_1_body" label="Pillar 1 Body" as="span" />
        <RichTextContent path="strategycontent.pillar_1_tags" label="Pillar 1 Tags" as="span" />
        <RichTextContent path="strategycontent.pillar_2_title" label="Pillar 2 Title" as="span" />
        <RichTextContent path="strategycontent.pillar_2_body" label="Pillar 2 Body" as="span" />
        <RichTextContent path="strategycontent.pillar_2_tags" label="Pillar 2 Tags" as="span" />
        <RichTextContent path="strategycontent.pillar_3_title" label="Pillar 3 Title" as="span" />
        <RichTextContent path="strategycontent.pillar_3_body" label="Pillar 3 Body" as="span" />
        <RichTextContent path="strategycontent.pillar_3_tags" label="Pillar 3 Tags" as="span" />
        <RichTextContent path="strategycontent.why_1_title" label="Why 1 Title" as="span" />
        <RichTextContent path="strategycontent.why_1_body" label="Why 1 Body" as="span" />
        <RichTextContent path="strategycontent.why_2_title" label="Why 2 Title" as="span" />
        <RichTextContent path="strategycontent.why_2_body" label="Why 2 Body" as="span" />
        <RichTextContent path="strategycontent.why_3_title" label="Why 3 Title" as="span" />
        <RichTextContent path="strategycontent.why_3_body" label="Why 3 Body" as="span" />
        <RichTextContent path="strategycontent.why_4_title" label="Why 4 Title" as="span" />
        <RichTextContent path="strategycontent.why_4_body" label="Why 4 Body" as="span" />
        <RichTextContent path="strategycontent.cap_1_title" label="Capability 1 Title" as="span" />
        <RichTextContent path="strategycontent.cap_1_body" label="Capability 1 Body" as="span" />
        <RichTextContent path="strategycontent.cap_2_title" label="Capability 2 Title" as="span" />
        <RichTextContent path="strategycontent.cap_2_body" label="Capability 2 Body" as="span" />
        <RichTextContent path="strategycontent.cap_3_title" label="Capability 3 Title" as="span" />
        <RichTextContent path="strategycontent.cap_3_body" label="Capability 3 Body" as="span" />
        <RichTextContent path="strategycontent.cap_4_title" label="Capability 4 Title" as="span" />
        <RichTextContent path="strategycontent.cap_4_body" label="Capability 4 Body" as="span" />
        <RichTextContent path="strategycontent.cap_5_title" label="Capability 5 Title" as="span" />
        <RichTextContent path="strategycontent.cap_5_body" label="Capability 5 Body" as="span" />
        <RichTextContent path="strategycontent.cap_6_title" label="Capability 6 Title" as="span" />
        <RichTextContent path="strategycontent.cap_6_body" label="Capability 6 Body" as="span" />
        <RichTextContent path="strategycontent.stat_1_value" label="Stat 1 Value" as="span" />
        <RichTextContent path="strategycontent.stat_1_label" label="Stat 1 Label" as="span" />
        <RichTextContent path="strategycontent.stat_2_value" label="Stat 2 Value" as="span" />
        <RichTextContent path="strategycontent.stat_2_label" label="Stat 2 Label" as="span" />
        <RichTextContent path="strategycontent.stat_3_value" label="Stat 3 Value" as="span" />
        <RichTextContent path="strategycontent.stat_3_label" label="Stat 3 Label" as="span" />
        <RichTextContent path="strategycontent.stat_4_value" label="Stat 4 Value" as="span" />
        <RichTextContent path="strategycontent.stat_4_label" label="Stat 4 Label" as="span" />
        <RichTextContent path="strategycontent.stat_5_value" label="Stat 5 Value" as="span" />
        <RichTextContent path="strategycontent.stat_5_label" label="Stat 5 Label" as="span" />
        <RichTextContent path="strategycontent.stat_6_value" label="Stat 6 Value" as="span" />
        <RichTextContent path="strategycontent.stat_6_label" label="Stat 6 Label" as="span" />
        <RichTextContent path="strategycontent.faq_1_q" label="FAQ 1 Question" as="span" />
        <RichTextContent path="strategycontent.faq_1_a" label="FAQ 1 Answer" as="span" />
        <RichTextContent path="strategycontent.faq_2_q" label="FAQ 2 Question" as="span" />
        <RichTextContent path="strategycontent.faq_2_a" label="FAQ 2 Answer" as="span" />
        <RichTextContent path="strategycontent.faq_3_q" label="FAQ 3 Question" as="span" />
        <RichTextContent path="strategycontent.faq_3_a" label="FAQ 3 Answer" as="span" />
        <RichTextContent path="strategycontent.faq_4_q" label="FAQ 4 Question" as="span" />
        <RichTextContent path="strategycontent.faq_4_a" label="FAQ 4 Answer" as="span" />
        <RichTextContent path="strategycontent.faq_5_q" label="FAQ 5 Question" as="span" />
        <RichTextContent path="strategycontent.faq_5_a" label="FAQ 5 Answer" as="span" />
        <EditableSlotGroup groupPath="strategycontent.why_2_image" definition={why2ImageDef}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="strategycontent.why_4_image" definition={why4ImageDef}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="strategycontent.cap_1_image" definition={cap1ImageDef}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="strategycontent.cap_2_image" definition={cap2ImageDef}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="strategycontent.cap_3_image" definition={cap3ImageDef}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="strategycontent.cap_4_image" definition={cap4ImageDef}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="strategycontent.cap_5_image" definition={cap5ImageDef}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="strategycontent.cap_6_image" definition={cap6ImageDef}>
          <span />
        </EditableSlotGroup>
      </div>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'StrategyContent',
  displayName: 'Strategy Content',
  tokens: {
    'strategycontent.intro_headline': {
      label: 'Intro Headline',
      type: 'richtext',
      defaultValue:
        '<p>Simplify complexity. <span>Scale with confidence.</span></p>',
    },
    'strategycontent.intro_body_1': {
      label: 'Intro Body 1',
      type: 'richtext',
      defaultValue:
        '<p>We help multifamily investors and operators navigate the challenges of growth by aligning investment strategy with hands-on management. From acquisition through daily operations, every decision is intentional, disciplined, and engineered to help your assets thrive.</p>',
    },
    'strategycontent.intro_body_2': {
      label: 'Intro Body 2',
      type: 'richtext',
      defaultValue:
        "<p>Whether you're preparing for a fund launch, refreshing a legacy portfolio, or scaling into new markets, growth requires authority. Solve operational hurdles, create real resident experiences, and build a portfolio that reflects your true competitive edge.</p>",
    },
    'strategycontent.pillar_1_title': {
      label: 'Pillar 1 Title',
      type: 'richtext',
      defaultValue: '<p>Invest with clarity</p>',
    },
    'strategycontent.pillar_1_body': {
      label: 'Pillar 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>Great real estate starts with great decisions. We identify high-growth Sunbelt markets and back every acquisition with rigorous analysis and deep local expertise — so every dollar is deployed where it can create the most lasting value.</p>',
    },
    'strategycontent.pillar_1_tags': {
      label: 'Pillar 1 Tags',
      type: 'richtext',
      defaultValue:
        '<p>Market Selection &amp; Sourcing|Underwriting &amp; Analysis|Due Diligence|Capital Structuring|Acquisition Strategy</p>',
    },
    'strategycontent.pillar_2_title': {
      label: 'Pillar 2 Title',
      type: 'richtext',
      defaultValue: '<p>Build with purpose</p>',
    },
    'strategycontent.pillar_2_body': {
      label: 'Pillar 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>From ground-up construction to value-add renovations, our in-house teams execute with precision — elevating quality, appeal, and long-term asset value across every property we own.</p>',
    },
    'strategycontent.pillar_2_tags': {
      label: 'Pillar 2 Tags',
      type: 'richtext',
      defaultValue:
        '<p>Construction Management|Interior Design &amp; Renovation|Capital Project Planning|Vendor &amp; Cost Management|Quality Assurance</p>',
    },
    'strategycontent.pillar_3_title': {
      label: 'Pillar 3 Title',
      type: 'richtext',
      defaultValue: '<p>Manage with excellence</p>',
    },
    'strategycontent.pillar_3_body': {
      label: 'Pillar 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>Hands-on, resident-focused management keeps communities running at their best — delivering consistent experiences and driving long-term performance across the entire portfolio.</p>',
    },
    'strategycontent.pillar_3_tags': {
      label: 'Pillar 3 Tags',
      type: 'richtext',
      defaultValue:
        '<p>Property Management|Portfolio Management|Revenue Management|Resident Experience|Capital Raising &amp; Investor Relations</p>',
    },
    'strategycontent.why_headline': {
      label: 'Why Headline',
      type: 'richtext',
      defaultValue: '<p>Why Our Strategy Delivers Results</p>',
    },
    'strategycontent.why_body': {
      label: 'Why Body',
      type: 'richtext',
      defaultValue:
        '<p>American Landmark is vertically integrated and believes that in-house capabilities lead to greater control and reduced risk. American Landmark has expertise in investments, construction management and design, property management, portfolio management, revenue management, and capital raising.</p>',
    },
    'strategycontent.why_1_title': {
      label: 'Why 1 Title',
      type: 'richtext',
      defaultValue: '<p>Focused on the Right Markets</p>',
    },
    'strategycontent.why_1_body': {
      label: 'Why 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>We invest in high-growth Sunbelt markets with strong economic fundamentals and long-term demand for multifamily housing. Our local expertise helps us identify opportunities with lasting potential.</p>',
    },
    'strategycontent.why_2_title': {
      label: 'Why 2 Title',
      type: 'richtext',
      defaultValue: '<p>Disciplined Investment Decisions</p>',
    },
    'strategycontent.why_2_body': {
      label: 'Why 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>Every investment is backed by rigorous analysis, market insight, and a long-term perspective. We make informed decisions that reduce risk and position communities for sustainable growth.</p>',
    },
    'strategycontent.why_3_title': {
      label: 'Why 3 Title',
      type: 'richtext',
      defaultValue: '<p>Value Through Transformation</p>',
    },
    'strategycontent.why_3_body': {
      label: 'Why 3 Body',
      type: 'richtext',
      defaultValue:
        "<p>We unlock each property's full potential through strategic renovations, operational improvements, and resident-focused enhancements that strengthen both community appeal and asset value.</p>",
    },
    'strategycontent.why_4_title': {
      label: 'Why 4 Title',
      type: 'richtext',
      defaultValue: '<p>Hands-On Management</p>',
    },
    'strategycontent.why_4_body': {
      label: 'Why 4 Body',
      type: 'richtext',
      defaultValue:
        '<p>Our integrated teams work together to optimize operations, elevate the resident experience, and drive long-term performance. From acquisition to daily management, every decision is focused on creating lasting value.</p>',
    },
    'strategycontent.platform_headline': {
      label: 'Platform Headline',
      type: 'richtext',
      defaultValue: '<p>Vertically Integrated. Better Connected.</p>',
    },
    'strategycontent.platform_body': {
      label: 'Platform Body',
      type: 'richtext',
      defaultValue:
        '<p>American Landmark is vertically integrated and believes that in-house capabilities lead to greater control and reduced risk. American Landmark has expertise in investments, construction management and design, property management, portfolio management, revenue management, and capital raising.</p>',
    },
    'strategycontent.cap_1_title': {
      label: 'Capability 1 Title',
      type: 'richtext',
      defaultValue: '<p>Investments</p>',
    },
    'strategycontent.cap_1_body': {
      label: 'Capability 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>We source and underwrite multifamily opportunities in high-growth Sunbelt markets, leveraging deep market intelligence to identify assets with strong upside and durable, long-term demand.</p>',
    },
    'strategycontent.cap_2_title': {
      label: 'Capability 2 Title',
      type: 'richtext',
      defaultValue: '<p>Construction Management &amp; Design</p>',
    },
    'strategycontent.cap_2_body': {
      label: 'Capability 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>Our in-house construction and design teams execute renovations and capital projects that elevate community appeal, quality, and long-term asset value across every property we own.</p>',
    },
    'strategycontent.cap_3_title': {
      label: 'Capability 3 Title',
      type: 'richtext',
      defaultValue: '<p>Property Management</p>',
    },
    'strategycontent.cap_3_body': {
      label: 'Capability 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>Hands-on, resident-focused management keeps communities running smoothly and to a high standard, delivering a consistent experience across every property in our portfolio.</p>',
    },
    'strategycontent.cap_4_title': {
      label: 'Capability 4 Title',
      type: 'richtext',
      defaultValue: '<p>Portfolio Management</p>',
    },
    'strategycontent.cap_4_body': {
      label: 'Capability 4 Body',
      type: 'richtext',
      defaultValue:
        '<p>Centralized oversight of asset performance, reporting, and strategy ensures every community aligns with broader portfolio objectives and delivers on investor goals.</p>',
    },
    'strategycontent.cap_5_title': {
      label: 'Capability 5 Title',
      type: 'richtext',
      defaultValue: '<p>Revenue Management</p>',
    },
    'strategycontent.cap_5_body': {
      label: 'Capability 5 Body',
      type: 'richtext',
      defaultValue:
        '<p>Data-driven pricing and revenue strategies optimize occupancy and returns while keeping rents competitive and responsive to real-time market conditions.</p>',
    },
    'strategycontent.cap_6_title': {
      label: 'Capability 6 Title',
      type: 'richtext',
      defaultValue: '<p>Capital Raising</p>',
    },
    'strategycontent.cap_6_body': {
      label: 'Capability 6 Body',
      type: 'richtext',
      defaultValue:
        '<p>We partner with institutional and private investors to deploy capital efficiently, aligning interests and delivering transparent, long-term value creation.</p>',
    },
    'strategycontent.advantage_eyebrow': {
      label: 'Advantage Eyebrow',
      type: 'richtext',
      defaultValue: '<p>The integrated advantage</p>',
    },
    'strategycontent.advantage_body': {
      label: 'Advantage Body',
      type: 'richtext',
      defaultValue:
        "<p>Build value that endures. An integrated approach to real estate means every team, every decision, and every dollar works together—so your assets don't just perform today, they compound for years to come.</p>",
    },
    'strategycontent.stats_eyebrow': {
      label: 'Stats Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Results, quantified</p>',
    },
    'strategycontent.stats_headline': {
      label: 'Stats Headline',
      type: 'richtext',
      defaultValue: '<p>Proof in the numbers.</p>',
    },
    'strategycontent.stat_1_value': {
      label: 'Stat 1 Value',
      type: 'richtext',
      defaultValue: '<p>10,000+</p>',
    },
    'strategycontent.stat_1_label': {
      label: 'Stat 1 Label',
      type: 'richtext',
      defaultValue: '<p>Residents served across our communities</p>',
    },
    'strategycontent.stat_2_value': {
      label: 'Stat 2 Value',
      type: 'richtext',
      defaultValue: '<p>30+</p>',
    },
    'strategycontent.stat_2_label': {
      label: 'Stat 2 Label',
      type: 'richtext',
      defaultValue: '<p>Years of multifamily excellence</p>',
    },
    'strategycontent.stat_3_value': {
      label: 'Stat 3 Value',
      type: 'richtext',
      defaultValue: '<p>8</p>',
    },
    'strategycontent.stat_3_label': {
      label: 'Stat 3 Label',
      type: 'richtext',
      defaultValue: '<p>States served across the Sunbelt</p>',
    },
    'strategycontent.stat_4_value': {
      label: 'Stat 4 Value',
      type: 'richtext',
      defaultValue: '<p>35+</p>',
    },
    'strategycontent.stat_4_label': {
      label: 'Stat 4 Label',
      type: 'richtext',
      defaultValue: '<p>Communities in our portfolio</p>',
    },
    'strategycontent.stat_5_value': {
      label: 'Stat 5 Value',
      type: 'richtext',
      defaultValue: '<p>500+</p>',
    },
    'strategycontent.stat_5_label': {
      label: 'Stat 5 Label',
      type: 'richtext',
      defaultValue: '<p>Team members nationwide</p>',
    },
    'strategycontent.stat_6_value': {
      label: 'Stat 6 Value',
      type: 'richtext',
      defaultValue: '<p>100%</p>',
    },
    'strategycontent.stat_6_label': {
      label: 'Stat 6 Label',
      type: 'richtext',
      defaultValue: '<p>Vertically integrated platform</p>',
    },
    'strategycontent.faq_eyebrow': {
      label: 'FAQ Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Services FAQ</p>',
    },
    'strategycontent.faq_headline': {
      label: 'FAQ Headline',
      type: 'richtext',
      defaultValue: '<p>The answers you need.</p>',
    },
    'strategycontent.faq_1_q': {
      label: 'FAQ 1 Question',
      type: 'richtext',
      defaultValue: '<p>What services does American Landmark offer?</p>',
    },
    'strategycontent.faq_1_a': {
      label: 'FAQ 1 Answer',
      type: 'richtext',
      defaultValue:
        '<p>American Landmark is a fully integrated multifamily platform. We provide in-house expertise across investments, construction management and design, property management, portfolio management, revenue management, and capital raising. Every capability is housed under one roof, giving us greater control and accountability at every stage of the asset lifecycle.</p>',
    },
    'strategycontent.faq_2_q': {
      label: 'FAQ 2 Question',
      type: 'richtext',
      defaultValue: '<p>How does vertical integration benefit investors?</p>',
    },
    'strategycontent.faq_2_a': {
      label: 'FAQ 2 Answer',
      type: 'richtext',
      defaultValue:
        '<p>Vertical integration means we control every aspect of the investment—from acquisition and underwriting through construction, operations, and disposition. This reduces reliance on third parties, lowers costs, speeds up execution, and ensures every decision aligns with our overall strategy. The result is better risk-adjusted returns and more transparent reporting for our investors.</p>',
    },
    'strategycontent.faq_3_q': {
      label: 'FAQ 3 Question',
      type: 'richtext',
      defaultValue: '<p>What markets does American Landmark focus on?</p>',
    },
    'strategycontent.faq_3_a': {
      label: 'FAQ 3 Answer',
      type: 'richtext',
      defaultValue:
        '<p>We focus on high-growth Sunbelt markets with strong economic fundamentals, job growth, and long-term demand for multifamily housing. Our portfolio spans eight states across the Southeast and Sunbelt, where our local expertise helps us identify opportunities with lasting potential.</p>',
    },
    'strategycontent.faq_4_q': {
      label: 'FAQ 4 Question',
      type: 'richtext',
      defaultValue: '<p>Can American Landmark manage properties we already own?</p>',
    },
    'strategycontent.faq_4_a': {
      label: 'FAQ 4 Answer',
      type: 'richtext',
      defaultValue:
        '<p>Our property management capabilities are primarily dedicated to communities within our own portfolio. However, we welcome conversations with property owners and investors who are interested in partnering with us on acquisitions or joint ventures where our integrated platform can add meaningful value.</p>',
    },
    'strategycontent.faq_5_q': {
      label: 'FAQ 5 Question',
      type: 'richtext',
      defaultValue: '<p>How does American Landmark approach value-add renovations?</p>',
    },
    'strategycontent.faq_5_a': {
      label: 'FAQ 5 Answer',
      type: 'richtext',
      defaultValue:
        '<p>Our in-house construction and design teams develop tailored renovation plans for each community based on market analysis, resident feedback, and return-on-investment modeling. We prioritize high-impact improvements—both interior and exterior—that elevate the resident experience, improve curb appeal, and strengthen long-term asset value.</p>',
    },
  },
  slotGroups: {
    'strategycontent.contact_cta': contactCtaDef,
    'strategycontent.partner_cta': partnerCtaDef,
    'strategycontent.why_2_image': why2ImageDef,
    'strategycontent.why_4_image': why4ImageDef,
    'strategycontent.property_strip': propertyStripDef,
    'strategycontent.cap_1_image': cap1ImageDef,
    'strategycontent.cap_2_image': cap2ImageDef,
    'strategycontent.cap_3_image': cap3ImageDef,
    'strategycontent.cap_4_image': cap4ImageDef,
    'strategycontent.cap_5_image': cap5ImageDef,
    'strategycontent.cap_6_image': cap6ImageDef,
  },
};

(StrategyContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default StrategyContent;
